import type { ReservedAsset } from '@/services/cart';

export interface MaintenanceCartItem {
  /** Stable key for React list + remove operations */
  key: string;
  name: string;
  lin: string;
  nsn: string;
  catalogId: string | null;
  sizeLabel: string | null;
  assetIds: string[];
  quantity: number;
}

const normalizeText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Group reserved assets for maintenance cart display.
 *
 * Important: inventory rows are separated by catalog_id (often representing size).
 * Grouping by catalog_id prevents different sizes from being merged in the cart.
 */
export const groupMaintenanceCartItems = (assets: ReservedAsset[]): MaintenanceCartItem[] => {
  const groups = new Map<string, Omit<MaintenanceCartItem, 'quantity'> & { quantity?: never }>();

  for (const reserved of assets) {
    const entity = reserved.entity;

    const name =
      normalizeText(entity.display_name) ??
      normalizeText(entity.entity_data?.asset_name) ??
      'Unknown';
    const lin = normalizeText(entity.entity_data?.lin) ?? '—';
    const nsn = normalizeText(entity.entity_data?.nsn) ?? '—';
    const catalogId = normalizeText(entity.entity_data?.catalog_id);
    const sizeLabel = normalizeText(entity.entity_data?.size);

    const key = `${catalogId ?? 'no-catalog'}|${name}|${lin}|${nsn}`;

    const existing = groups.get(key);
    if (existing) {
      existing.assetIds.push(entity.id);
    } else {
      groups.set(key, {
        key,
        name,
        lin,
        nsn,
        catalogId: catalogId ?? null,
        sizeLabel,
        assetIds: [entity.id],
      });
    }
  }

  return Array.from(groups.values()).map((value) => ({
    ...value,
    quantity: value.assetIds.length,
  }));
};
